<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Password from "primevue/password";
import { ref, watch } from "vue";
import { useRouter } from "vue-router";

import FormErrors from "./FormErrors.vue";
import { useAsyncAction } from "../composables/useAsyncAction";
import { useSession } from "../composables/useSession";

/**
 * Signing in again without leaving the page.
 *
 * The session lasts an hour. Filling in a long form and losing it to a
 * redirect is the kind of thing that makes someone stop trusting the tool, so
 * an expired session is answered here rather than by navigating away: the
 * screen behind this dialog keeps whatever was typed into it.
 */
const { expired, expiredFor, signIn } = useSession();
const router = useRouter();

const username = ref("");
const password = ref("");

const { busy, errors, run, clear } = useAsyncAction(signIn);

// Whoever was signed in is almost certainly signing in again.
watch(expired, (isExpired) => {
  if (isExpired) {
    username.value = expiredFor.value;
    password.value = "";
    clear();
  }
});

async function confirm(): Promise<void> {
  if (password.value === "") {
    return;
  }

  await run(username.value, password.value);
  password.value = "";
}

async function toSignIn(): Promise<void> {
  await router.push({ name: "anmelden" });
}
</script>

<template>
  <Dialog
    :visible="expired"
    modal
    :closable="false"
    :draggable="false"
    header="Die Anmeldung ist abgelaufen"
    :style="{ width: '24rem', maxWidth: '95vw' }"
  >
    <p>
      Aus Sicherheitsgründen endet die Anmeldung nach einer Stunde. Melde dich
      erneut an – was du gerade ausgefüllt hast, bleibt erhalten.
    </p>

    <form class="form-grid" @submit.prevent="confirm">
      <FormErrors :messages="errors" />

      <div class="field">
        <label for="wieder-benutzer">Benutzername</label>
        <InputText
          id="wieder-benutzer"
          v-model="username"
          autocomplete="username"
        />
      </div>

      <div class="field">
        <label for="wieder-passwort">Passwort</label>
        <Password
          v-model="password"
          input-id="wieder-passwort"
          :feedback="false"
          toggle-mask
          :input-props="{ autocomplete: 'current-password' }"
          input-class="w-full"
          fluid
        />
      </div>

      <div class="form-actions">
        <Button
          type="submit"
          label="Weiter arbeiten"
          icon="pi pi-check"
          :loading="busy"
        />
        <Button
          type="button"
          label="Zur Anmeldung"
          severity="secondary"
          text
          @click="toSignIn"
        />
      </div>
    </form>
  </Dialog>
</template>
